/**
 * Covers: a scrim, a large headline, and the crop that decides where it sits.
 *
 * A cover is not a scaled-up caption. It diverges from the locked caption style
 * on background, line count, text width, line advance and word gap --
 * deliberately, and recorded as such in the spec. Casing does not diverge.
 *
 * Everything here is layout: which words go on which line, where each one
 * starts, and how dense the scrim is on every row. The page paints from the
 * same layout the ASS document is written from, so the painted cover and the
 * document cannot place a word differently.
 */

import { FORMAT_FIELDS, STYLE_FORMAT, dialogue, inlineColour, scriptInfo, validate } from "./assfile";
import type { Face } from "./font";
import { ACCENT_ON_DARK, assColour } from "./presets";
import { floorDiv, int, len, repr, round, roundTo, split, sum } from "./py";
import { isDangling, lineWidth } from "./shaping";
import { type Pixels, type Ratio, px, ratio } from "./units";

export const FONTSIZE = ratio(0.1);
/**
 * Of Fontsize -- 1.08 em. NOT the caption's 1.09, which is 1.44 em: that value
 * is structure (a box is one line advance tall), and covers have no boxes.
 */
export const LINE_ADVANCE = ratio(0.82);
/** Of the font's own space. Tighter than the caption's 0.85, so the words group as one. */
export const WORD_GAP = ratio(0.55);
/**
 * Of source width. Wider than the caption's 0.72, which is set by TikTok's
 * engagement rail -- and a rail does not crop a grid thumbnail.
 */
export const SAFE_WIDTH = ratio(0.88);
/** The only free number in either margin; the rest is derived. */
export const CLEARANCE = ratio(0.03);

export const MAX_LINES = 3;
/**
 * The smallest a headline may shrink to. Below this a cover headline stops
 * being readable as a grid thumbnail, which is the whole job.
 */
export const MIN_FONTSIZE = ratio(0.06);
export const SHRINK_STEP = ratio(0.005);

/** YouTube shows vertical video at 4:5 on home, explore and subscriptions. */
export const CROP: readonly [number, number] = [4, 5];
/** Instagram's profile grid shows vertical video as a centred 3:4 tile. */
export const GRID: readonly [number, number] = [3, 4];
/**
 * Of the grid tile's height, measured up from its bottom edge: how far the view
 * count Instagram draws over every tile reaches. Measured on an iPhone
 * screenshot of a profile grid on 2026-09-17 -- the digits' tops sit on row 474
 * of a 533-row tile, the same row on every tile. The count sits at the left,
 * but a centred headline line of any useful length reaches it, so the whole row
 * is treated as taken.
 */
export const GRID_COUNTER = ratio(0.111);

/** Where the headline sits in the frame. The scrim follows it. */
export const PLACEMENTS = ["low", "center", "top"] as const;
export type Placement = (typeof PLACEMENTS)[number];
/**
 * Low is the composition the covers were designed around, and stays the
 * default: the eye lands on the image first and reads the headline after it.
 */
export const DEFAULT_PLACEMENT: Placement = "low";

export const SCRIM_START = ratio(0.34);
/** Not 1.0: dead black under the headline stops it looking like a photograph. */
export const SCRIM_PEAK = 0.9;
/** The scrim's colour, as CSS. The same #18181C the ffmpeg filter used. */
export const SCRIM_RGB = "#18181C";
/**
 * A light scrim at the edge opposite the headline, for the platform chrome that
 * sits there. It is the weaker one; the headline's own edge gets the peak.
 */
export const FAR_SCRIM = 0.5;
export const FAR_SCRIM_END = ratio(0.18);
/**
 * A centred headline gets a gradient running edge to edge: full density within
 * CENTRE_FLAT of the middle -- three lines are 0.123 of the height either side
 * of centre, so the flat part covers them -- then a fall to nothing that
 * reaches zero exactly at the top and bottom edges. A band that ended short of
 * them drew two soft horizons across the frame.
 */
export const CENTRE_FLAT = ratio(0.15);
export const CENTRE_FADE = ratio(0.5);

export const TEXT_HEX = "#FFFFFF";
export const TEXT_COLOUR = assColour(TEXT_HEX);
export const ACCENT_COLOUR = assColour(ACCENT_ON_DARK);

/** Forces a line break at this point in a headline. It does not occur in Russian or English copy, unlike `/`. */
export const BREAK = "|";

const DANGLING_PENALTY = 40.0;
const STYLE_NAME = "Cover";

/** The headline will not fit, and is refused rather than shrunk further. */
export interface TooLong {
  readonly kind: "too-long";
  readonly message: string;
}

/**
 * The headline would be lost on a thumbnail: cut by YouTube's 4:5 crop, or
 * covered by the view counter on Instagram's profile grid.
 */
export interface Cropped {
  readonly kind: "cropped";
  readonly message: string;
}

export class CoverGeometry {
  private constructor(
    readonly width: number,
    readonly height: number,
    readonly fontsize: Pixels,
    readonly lineAdvance: Pixels,
    readonly advance: Pixels,
    readonly wordGap: Pixels,
    readonly safeWidth: Pixels,
    readonly textBottom: Pixels,
    readonly bottomMargin: Pixels,
    /**
     * The 4:5 crop edge plus clearance. The bottom margin is at least this, and
     * more where Instagram's grid counter reaches higher than the crop does.
     */
    readonly cropMargin: Pixels,
    readonly cropBand: readonly [number, number],
    readonly gridBand: readonly [number, number],
    /** The first row Instagram's view counter covers on the grid tile. */
    readonly counterTop: number,
    readonly lettersPerLine: number,
    readonly fontsizeRatio: Ratio,
    readonly face: Face,
    readonly placement: Placement,
  ) {}

  /**
   * The crop cuts both edges, so the top margin is the crop margin. The counter
   * only sits at the bottom, so it does not push the top down.
   */
  get textTop(): Pixels {
    return this.cropMargin;
  }

  /** How far the counter raised a low headline above the crop margin. */
  get lift(): number {
    return round(this.bottomMargin - this.cropMargin);
  }

  /**
   * The baseline of the last line, for a headline this many lines tall. A line
   * occupies one line advance, which is what makes the block's height knowable
   * before anything is drawn.
   */
  blockBottom(lines: number): Pixels {
    const block = lines * this.lineAdvance;
    if (this.placement === "top") return px(this.textTop + block);
    if (this.placement === "center") return px((this.height + block) / 2);
    return this.textBottom;
  }

  /** How wide this text sets at this size, glyph by glyph. */
  readonly textWidth = (text: string): Pixels => px(this.face.widthRatio(text) * this.fontsize);

  static fromSource(
    width: number,
    height: number,
    face: Face,
    fontsizeRatio: Ratio = FONTSIZE,
    placement: Placement = DEFAULT_PLACEMENT,
  ): CoverGeometry {
    const fontsize = height * fontsizeRatio;
    const advance = fontsize * face.advanceRatio;
    const space = face.widthRatio(" ") * fontsize;
    const safeWidth = width * SAFE_WIDTH;

    const keep = width / height / (CROP[0] / CROP[1]);
    const bandTop = (1 - keep) / 2;
    const crop: [number, number] = [round(bandTop * height), round((1 - bandTop) * height)];
    const cropMargin = height * (bandTop + CLEARANCE);

    const tile = width / height / (GRID[0] / GRID[1]);
    const tileTop = (1 - tile) / 2;
    const grid: [number, number] = [round(tileTop * height), round((1 - tileTop) * height)];
    const counterTop = (1 - tileTop - GRID_COUNTER * tile) * height;
    const counterMargin = height - counterTop + height * CLEARANCE;
    // Python's max() keeps the first of two equal values.
    const bottomMargin = counterMargin > cropMargin ? counterMargin : cropMargin;

    return new CoverGeometry(
      width,
      height,
      px(fontsize),
      px(fontsize * LINE_ADVANCE),
      px(advance),
      px(space * WORD_GAP),
      px(safeWidth),
      px(height - bottomMargin),
      px(bottomMargin),
      px(cropMargin),
      crop,
      grid,
      round(counterTop),
      int(floorDiv(safeWidth, advance)),
      fontsizeRatio,
      face,
      placement,
    );
  }
}

/** A parsed headline: the words, which are accented, where breaks are forced. */
export interface Headline {
  readonly text: string;
  /** Positions of accented words in `text.split()`. */
  readonly accents: ReadonlySet<number>;
  /** Positions of the words after which a line break is required. */
  readonly breaks: ReadonlySet<number>;
}

const MARKUP = /(\*[^*]+\*|\|)/u;

/**
 * Read `*…*` accents and `|` breaks out of an operator's headline.
 *
 * Both are matched by offset rather than by splitting on the markers, so
 * punctuation sitting just outside a span stays attached to the word it belongs
 * to: `*сам себя*.` is two words, the second being `себя.`. Offsets are UTF-16
 * units where Python's are code points; every offset is only ever compared with
 * another, and the mapping between the two preserves order, so the result is
 * the same.
 */
export function parseHeadline(raw: string): Headline {
  const clean: string[] = [];
  const accentRanges: [number, number][] = [];
  const breakOffsets: number[] = [];
  let cursor = 0;
  for (const chunk of raw.split(MARKUP)) {
    if (!chunk) continue;
    if (chunk === BREAK) {
      breakOffsets.push(cursor);
    } else if (chunk.startsWith("*") && chunk.endsWith("*") && len(chunk) > 2) {
      const body = chunk.slice(1, -1);
      accentRanges.push([cursor, cursor + body.length]);
      clean.push(body);
      cursor += body.length;
    } else {
      clean.push(chunk);
      cursor += chunk.length;
    }
  }

  const joined = clean.join("");
  const spans: [number, number][] = [];
  let offset = 0;
  for (const word of split(joined)) {
    offset = joined.indexOf(word, offset);
    spans.push([offset, offset + word.length]);
    offset += word.length;
  }

  const accents = new Set<number>();
  spans.forEach(([begin, finish], index) => {
    if (accentRanges.some(([a, b]) => begin < b && a < finish)) accents.add(index);
  });
  const breaks = new Set<number>();
  for (const marker of breakOffsets) {
    let last = -1;
    spans.forEach(([, finish], index) => {
      if (finish <= marker) last = Math.max(last, index);
    });
    if (last >= 0 && last < spans.length - 1) breaks.add(last);
  }

  return { text: split(joined).join(" "), accents, breaks };
}

/** Every `count`-subset of `positions`, in itertools.combinations order. */
function combinations(positions: readonly number[], count: number): number[][] {
  if (count === 0) return [[]];
  const out: number[][] = [];
  positions.forEach((first, index) => {
    for (const rest of combinations(positions.slice(index + 1), count - 1)) out.push([first, ...rest]);
  });
  return out;
}

/** Every way of cutting the words into lines that honours the forced breaks, in Python's order. */
function arrangements(words: readonly string[], maxLines: number, breaks: ReadonlySet<number>): string[][][] {
  const required = new Set<number>();
  for (const index of breaks) if (index >= 0 && index < words.length - 1) required.add(index + 1);
  if (required.size > maxLines - 1) return [];
  const positions = Array.from({ length: Math.max(words.length - 1, 0) }, (_, index) => index + 1);
  const out: string[][][] = [];
  for (let count = 0; count < maxLines; count++) {
    for (const cuts of combinations(positions, count)) {
      if (![...required].every((cut) => cuts.includes(cut))) continue;
      const bounds = [0, ...cuts, words.length];
      const lines: string[][] = [];
      for (let index = 0; index + 1 < bounds.length; index++) {
        lines.push(words.slice(bounds[index], bounds[index + 1]));
      }
      out.push(lines);
    }
  }
  return out;
}

/** A headline broken into lines at the size `geometry` sets it. */
export interface Fitted {
  readonly kind: "fitted";
  readonly lines: string[][];
  readonly geometry: CoverGeometry;
}

/**
 * Break a headline into lines, measured in pixels rather than characters.
 *
 * Counting characters and treating a space as one full advance -- the obvious
 * shortcut -- throws away exactly the capacity the tightened gap buys back.
 */
export function fitHeadline(
  text: string,
  geometry: CoverGeometry,
  breaks: ReadonlySet<number> = new Set(),
): Fitted | TooLong {
  const words = split(text);
  if (!words.length) return { kind: "too-long", message: "the headline is empty" };

  let best: { cost: number; lines: string[][] } | null = null;
  for (const lines of arrangements(words, MAX_LINES, breaks)) {
    const widths = lines.map((line) => lineWidth(line, geometry.textWidth, geometry.wordGap));
    const widest = Math.max(...widths);
    if (widest > geometry.safeWidth) continue;
    let cost = (widest - Math.min(...widths)) / geometry.advance;
    cost += sum(lines.slice(0, -1).flatMap((line) => (isDangling(line[line.length - 1] ?? "") ? [DANGLING_PENALTY] : [])));
    if (best === null || cost < best.cost) best = { cost, lines };
  }

  if (best === null) {
    return {
      kind: "too-long",
      message:
        `${repr(text)} does not fit: the budget is ${geometry.lettersPerLine} letters ` +
        `per line across at most ${MAX_LINES} lines`,
    };
  }
  return { kind: "fitted", lines: best.lines, geometry };
}

/**
 * Fit a headline at the largest size that works, shrinking if it must.
 *
 * The shrink is not silent: the size used is reported to the operator. It
 * stops at MIN_FONTSIZE, below which a cover headline is no longer readable as
 * a grid thumbnail and a refusal is the honest answer.
 */
export function fitHeadlineScaled(
  text: string,
  width: number,
  height: number,
  face: Face,
  breaks: ReadonlySet<number> = new Set(),
  placement: Placement = DEFAULT_PLACEMENT,
): Fitted | TooLong {
  let size = FONTSIZE;
  while (size >= MIN_FONTSIZE - 1e-9) {
    const geometry = CoverGeometry.fromSource(width, height, face, size, placement);
    const fit = fitHeadline(text, geometry, breaks);
    if (fit.kind === "fitted") return fit;
    size = ratio(roundTo(size - SHRINK_STEP, 4));
  }
  const smallest = CoverGeometry.fromSource(width, height, face, MIN_FONTSIZE, placement);
  return {
    kind: "too-long",
    message:
      `${repr(text)} does not fit even at the smallest size: the budget there is ` +
      `${smallest.lettersPerLine} letters per line across at most ${MAX_LINES} lines`,
  };
}

/** A colour in both forms the cover uses it in. */
export interface Colour {
  /** `&H00BBGGRR`, as the ASS style table writes it. */
  readonly ass: string;
  /** `#RRGGBB`, for a canvas. */
  readonly css: string;
}

function colour(ass: string): Colour {
  const body = ass.slice(4);
  return { ass, css: `#${body.slice(4, 6)}${body.slice(2, 4)}${body.slice(0, 2)}` };
}

/** One headline word, where libass draws it. */
export interface PlacedWord {
  readonly text: string;
  /** Position in the headline, counted across lines: what an accent refers to. */
  readonly index: number;
  readonly line: number;
  /** Left edge before rounding, in source pixels. */
  readonly x: Pixels;
  /** Baseline before rounding, in source pixels. */
  readonly baseline: Pixels;
  /** The `\an1\pos` anchor libass draws at: bottom left, rounded as the document writes it. */
  readonly pos: { readonly x: number; readonly y: number };
  readonly accent: boolean;
  readonly colour: Colour;
}

export interface HeadlineLayout {
  readonly geometry: CoverGeometry;
  readonly lines: readonly (readonly string[])[];
  readonly words: readonly PlacedWord[];
  /** The Fontsize libass is given: `round(geometry.fontsize)`. */
  readonly fontsize: number;
  readonly family: string;
}

/** Place every word of fitted lines. The ASS document and the page both draw from this. */
export function placeHeadline(
  geometry: CoverGeometry,
  lines: readonly (readonly string[])[],
  accents: ReadonlySet<number> = new Set(),
  accentColour: string | null = null,
): HeadlineLayout {
  const highlight = colour(accentColour ? assColour(accentColour) : ACCENT_COLOUR);
  const plain = colour(TEXT_COLOUR);
  const words: PlacedWord[] = [];
  const count = lines.length;
  let position = 0;
  lines.forEach((line, index) => {
    const width = lineWidth(line, geometry.textWidth, geometry.wordGap);
    let x = px((geometry.width - width) / 2);
    const baseline = px(geometry.blockBottom(count) - (count - 1 - index) * geometry.lineAdvance);
    for (const text of line) {
      const accent = accents.has(position);
      words.push({
        text,
        index: position,
        line: index,
        x,
        baseline,
        pos: { x: round(x), y: round(baseline) },
        accent,
        colour: accent ? highlight : plain,
      });
      position += 1;
      x = px(x + geometry.textWidth(text) + geometry.wordGap);
    }
  });
  return {
    geometry,
    lines,
    words,
    fontsize: round(geometry.fontsize),
    family: geometry.face.family,
  };
}

/** A single-event-per-word ASS document for a placed headline. */
export function coverDocument(layout: HeadlineLayout): string {
  const { geometry } = layout;
  const events = layout.words.map((word) =>
    dialogue(
      "0",
      "0:00:00.00",
      "9:59:59.99",
      STYLE_NAME,
      "",
      "0",
      "0",
      "0",
      "",
      `{\\an1\\pos(${word.pos.x},${word.pos.y})\\1c${inlineColour(word.colour.ass)}\\bord0\\shad0}${word.text}`,
    ),
  );
  const document =
    scriptInfo(geometry.width, geometry.height) +
    "\n[V4+ Styles]\n" +
    `${STYLE_FORMAT}\n` +
    `Style: ${STYLE_NAME},${layout.family},${layout.fontsize},${TEXT_COLOUR},` +
    `${TEXT_COLOUR},&H00000000,&H00000000,-1,0,0,0,100,100,0,0,1,0,0,1,0,0,0,204\n` +
    "\n[Events]\n" +
    `Format: ${FORMAT_FIELDS.join(", ")}\n` +
    events.join("\n") +
    "\n";
  validate(document);
  return document;
}

/** `cover_ass`: place the lines and write them out. */
export function coverAss(
  geometry: CoverGeometry,
  lines: readonly (readonly string[])[],
  accents: ReadonlySet<number> = new Set(),
  accentColour: string | null = null,
): string {
  return coverDocument(placeHeadline(geometry, lines, accents, accentColour));
}

/**
 * One smootherstep of the scrim: transparent at `start`, `peak` at `full`.
 * `around` measures the ramp from the distance to a row rather than from the
 * row itself, which is what makes a centred band one ramp instead of two that
 * fight each other.
 */
export interface Ramp {
  readonly start: number;
  readonly full: number;
  readonly peak: number;
  readonly around: number | null;
}

/**
 * The scrim for this placement, as ramps over rows. The density sits under the
 * headline, wherever the headline is, and the opposite edge keeps the light
 * scrim that platform chrome needs.
 */
export function scrimRamps(geometry: CoverGeometry): Ramp[] {
  const { height } = geometry;
  const [cropTop, cropBottom] = geometry.cropBand;
  if (geometry.placement === "top") {
    return [
      { start: round((1 - SCRIM_START) * height), full: cropTop, peak: SCRIM_PEAK, around: null },
      { start: round((1 - FAR_SCRIM_END) * height), full: height, peak: FAR_SCRIM, around: null },
    ];
  }
  if (geometry.placement === "center") {
    return [
      {
        start: round(CENTRE_FADE * height),
        full: round(CENTRE_FLAT * height),
        peak: SCRIM_PEAK,
        around: round(height / 2),
      },
    ];
  }
  // Raised by the counter, the headline takes its ramp up with it, so it sits
  // on the same density it did when the crop alone set the margin.
  const lift = geometry.lift;
  return [
    { start: round(SCRIM_START * height) - lift, full: cropBottom - lift, peak: SCRIM_PEAK, around: null },
    { start: round(FAR_SCRIM_END * height), full: 0, peak: FAR_SCRIM, around: null },
  ];
}

/**
 * smootherstep, t^3(6t^2 - 15t + 10): flat at both ends, so the scrim has no
 * visible onset line. A power curve hits the same endpoints and does not.
 */
export function smootherstep(t: number): number {
  return t ** 3 * (t * (t * 6 - 15) + 10);
}

/** The scrim's alpha on one row: the densest of the ramps there. */
export function alphaAt(ramps: readonly Ramp[], row: number): number {
  let alpha = 0;
  for (const ramp of ramps) {
    const along = ramp.around === null ? row : Math.abs(row - ramp.around);
    const t = Math.min(Math.max((along - ramp.start) / (ramp.full - ramp.start), 0), 1);
    const value = smootherstep(t) * ramp.peak;
    if (value > alpha) alpha = value;
  }
  return alpha;
}

/** The scrim's alpha for every row of the frame. */
export function alphaProfile(geometry: CoverGeometry): Float64Array {
  const ramps = scrimRamps(geometry);
  const profile = new Float64Array(geometry.height);
  for (let row = 0; row < geometry.height; row++) profile[row] = alphaAt(ramps, row);
  return profile;
}

/** The rectangle enclosing every drawn pixel of the headline, in source-frame pixels. */
export interface Ink {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** Where the headline's pixels landed against the 4:5 crop band and the grid's view counter. */
export interface CropCheck {
  readonly kind: "clear";
  readonly box: Ink;
  readonly clearance: number;
  readonly counterClearance: number;
  readonly ok: boolean;
}

/**
 * Judge the drawn headline against the crop band, per spec 17.7. The box is
 * measured from the painted text alone, never calculated: arithmetic agreeing
 * with itself proves nothing.
 */
export function checkCrop(box: Ink, geometry: CoverGeometry): CropCheck {
  const [top, bottom] = geometry.cropBand;
  const clearance = Math.min(box.top - top, bottom - box.bottom);
  const counterClearance = geometry.counterTop - box.bottom;
  return {
    kind: "clear",
    box,
    clearance,
    counterClearance,
    ok: box.top >= top && box.bottom <= bottom && counterClearance >= 0,
  };
}

/** Refuse a cover whose headline the crop would cut or the counter cover. */
export function requireUncropped(box: Ink, geometry: CoverGeometry): CropCheck | Cropped {
  const result = checkCrop(box, geometry);
  const [top, bottom] = geometry.cropBand;
  if (box.top < top || box.bottom > bottom) {
    return {
      kind: "cropped",
      message:
        `the headline reaches y${box.top}-${box.bottom}, outside ` +
        `YouTube's 4:5 crop band y${top}-${bottom}; it would be cut on the home, ` +
        "explore and subscription pages",
    };
  }
  if (result.counterClearance < 0) {
    return {
      kind: "cropped",
      message:
        `the headline reaches down to y${box.bottom}, past y${geometry.counterTop} ` +
        "where Instagram draws the view counter on the profile grid; its last line " +
        "would be covered there",
    };
  }
  return result;
}

/** Everything the page needs to draw a cover over a frame. */
export interface CoverPlan {
  readonly kind: "planned";
  readonly headline: Headline;
  readonly layout: HeadlineLayout;
  readonly ramps: readonly Ramp[];
  /** Whether the headline had to shrink below FONTSIZE to fit. */
  readonly shrunk: boolean;
}

/** Parse, fit, place and scrim an operator's headline for one frame size. */
export function planCover(
  raw: string,
  width: number,
  height: number,
  face: Face,
  placement: Placement = DEFAULT_PLACEMENT,
  accentColour: string | null = null,
): CoverPlan | TooLong {
  const headline = parseHeadline(raw);
  const fit = fitHeadlineScaled(headline.text, width, height, face, headline.breaks, placement);
  if (fit.kind === "too-long") return fit;
  return {
    kind: "planned",
    headline,
    layout: placeHeadline(fit.geometry, fit.lines, headline.accents, accentColour),
    ramps: scrimRamps(fit.geometry),
    shrunk: fit.geometry.fontsizeRatio < FONTSIZE,
  };
}
